import { Link } from 'react-router-dom';
import { Card } from '@thegpvc/ui';
import { text, cx, colWidth } from '../../styles/theme';
import { Eyebrow } from './WorkDeliveredCard';
import type { AgentActivity, TimePeriod } from '../../types/api';

const PERIOD_DAYS: Record<string, number> = { today: 1, '7days': 7, '30days': 30 };

function fmtH(h: number): string {
  return `${h >= 10 ? h.toFixed(0) : h.toFixed(1)}h`;
}

/**
 * TimeLedgerCard answers "where did the time go?": your prompting hours vs
 * the agents' working hours as labeled bars, the ratio as a sentence, and
 * three sub-stats (peak concurrency, days with activity, delegated share).
 * Automated runs (evals, `claude -p`) get their own bar, outside the ratio.
 * Keyboard hours are prompt-anchored (hours in which you actually prompted)
 * — the retired sitting-window presence counted idle wall-clock.
 */
export function TimeLedgerCard({ data, period, loading }: { data?: AgentActivity; period: TimePeriod; loading: boolean }) {
  const agent = data?.agentHours ?? 0;
  const keyboard = data?.keyboardHours ?? 0;
  const sub = data?.subagentHours ?? 0;
  const peak = data?.peakConcurrency ?? 0;
  // Scripted runs (evals, `claude -p` loops): tracked, but kept out of the
  // ratio and the delegated share — nobody was driving them.
  const automated = data?.automatedHours ?? 0;
  const automatedRuns = data?.automatedRuns ?? 0;
  const daysActive = (data?.days ?? []).filter(
    (d) => parseInt(d.mainMs ?? '0', 10) + parseInt(d.subagentMs ?? '0', 10) > 0,
  ).length;
  const periodDays = PERIOD_DAYS[period];
  const delegatedPct = agent > 0 ? Math.round((sub / agent) * 100) : 0;
  const ratio = keyboard > 0 ? agent / keyboard : null;
  const max = Math.max(agent, keyboard, automated, 0.1);

  // A completed period with no agent AND no keyboard time is genuinely empty
  // (new account, or "today" before any activity). The hero row renders even
  // when Home shows its "No session activity" card below, so the zeroed bars,
  // "0/N days", and the "still being computed" sentence would sit there reading
  // as broken/pending. Show an explicit empty state instead. Distinct from
  // `loading`, which keeps its skeleton rendering below.
  if (!loading && agent === 0 && keyboard === 0 && automatedRuns === 0) {
    return (
      <Card className="h-full">
        <Card.Body className="flex h-full flex-col">
          <Eyebrow>Time ledger</Eyebrow>
          <div className="flex flex-1 items-center justify-center py-10">
            <p className={cx('text-center text-sm', text.secondary)}>No agent activity in this period.</p>
          </div>
        </Card.Body>
      </Card>
    );
  }

  const bar = (label: string, hours: number, cls: string) => (
    <div className="flex items-center gap-3">
      <span className={cx(colWidth.labelMd, 'shrink-0 text-sm leading-tight', text.secondary)}>{label}</span>
      <div className="h-3.5 flex-1 rounded-full bg-gray-100 dark:bg-navy-800">
        <div className={cx('h-full rounded-full', cls)} style={{ width: `${Math.max(2, (hours / max) * 100)}%` }} />
      </div>
      <span className={cx(colWidth.valueSm, 'shrink-0 text-right font-mono text-sm tabular-nums', text.primary)}>
        {loading ? '…' : fmtH(hours)}
      </span>
    </div>
  );

  const stat = (value: string, label: string) => (
    <div>
      <div className={cx('text-xl font-bold tabular-nums leading-none', text.primary)}>{value}</div>
      <div className={cx('mt-1 text-sm', text.secondary)}>{label}</div>
    </div>
  );

  return (
    <Card className="h-full">
      <Card.Body className="h-full">
        <div className="flex items-start justify-between">
          <Eyebrow>Time ledger</Eyebrow>
          <Link to="/stats?view=usage" className={cx('text-sm font-medium', text.link)}>
            Details
          </Link>
        </div>

        <p className={cx('mt-3 text-base font-medium', text.primary)}>
          {ratio === null ? (
            'Agents worked while your prompting hours are still being computed.'
          ) : (
            <>
              For every hour you spent at the keyboard, agents worked{' '}
              <span className="rounded bg-blue-50 px-1 font-mono tabular-nums text-blue-700 dark:bg-blue-900/30 dark:text-blue-300">
                {ratio.toFixed(1)}h
              </span>
              .
            </>
          )}
        </p>

        <div className="mt-4 space-y-2.5">
          {bar('You, actively driving', keyboard, 'bg-navy-900 dark:bg-navy-200')}
          {bar('Agents, working', agent, 'bg-blue-500')}
          {automatedRuns > 0 && bar('Automated runs', automated, 'bg-gray-300 dark:bg-navy-600')}
        </div>
        {automatedRuns > 0 && (
          <p className={cx('mt-2 text-sm', text.secondary)}>
            {automatedRuns.toLocaleString('en-US')} scripted run{automatedRuns === 1 ? '' : 's'} (evals,{' '}
            <code className="font-mono">claude -p</code>) — counted in usage, not in your ratio.
          </p>
        )}

        <div className="mt-5 flex flex-wrap gap-x-10 gap-y-3">
          {stat(String(peak), 'peak concurrent agents')}
          {periodDays
            ? stat(`${daysActive}/${periodDays}`, 'days with activity')
            : stat(String(daysActive), 'days with activity')}
          {stat(`${delegatedPct}%`, 'of agent time was delegated')}
        </div>
      </Card.Body>
    </Card>
  );
}
